import { useMemo, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { admin_import_path, data_admin_import_path, sync_admin_import_path } from '@/lib/routes'
import { MoreHorizontal } from 'lucide-react'
import AppLayout from '@/components/AppLayout'
import PageTitle from '@/components/PageTitle'
import { Button } from '@/components/ui/Button'
import { Card, CardContent } from '@/components/ui/Card'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/DropdownMenu'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/Table'
import ResetIncrementalModal from './components/ResetIncrementalModal'
import SyncSectionHeader from './components/SyncSectionHeader'
import DataSourceModal from './components/DataSourceModal'
import SourceLink from './components/SourceLink'
import { nodeLabel, type DataSource, type OrgNode } from './components/types'

interface Props {
  dataSources: DataSource[]
  processorClasses: string[]
  sheetProcessorClasses: string[]
  targetModels: string[]
  maxResyncLookbackDays: number
  orgNodes: OrgNode[]
}

function ActionsDropdown({
  onSync,
  onEdit,
  onDelete,
  onReset,
}: {
  onSync: () => void
  onEdit: () => void
  onDelete: () => void
  onReset?: () => void
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        >
          <MoreHorizontal size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px]">
        <DropdownMenuItem onSelect={onSync}>Sync Now</DropdownMenuItem>
        <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>
        {onReset && (
          <DropdownMenuItem onSelect={onReset} className="text-destructive">
            Reset…
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onDelete} className="text-destructive">
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function AnchorLine({ ds, nodesById }: { ds: DataSource; nodesById: Record<string, OrgNode> }) {
  const anchor = ds.org_node_id ? nodesById[ds.org_node_id] : null
  if (!anchor) return null
  return <div className="text-xs text-muted-foreground">Anchor: {nodeLabel(anchor, nodesById)}</div>
}

function DataSourceTable({
  dataSources,
  nodesById,
  onSync,
  onEdit,
  onDelete,
}: {
  dataSources: DataSource[]
  nodesById: Record<string, OrgNode>
  onSync: (ds: DataSource) => void
  onEdit: (ds: DataSource) => void
  onDelete: (ds: DataSource) => void
}) {
  if (dataSources.length === 0) return null

  return (
    <Card>
      <CardContent className="p-0">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-48">Target</TableHead>
              <TableHead className="w-24">Source</TableHead>
              <TableHead className="w-24">Interval</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dataSources.map((ds) => (
              <TableRow key={ds.id}>
                <TableCell>
                  <div className="font-medium">{ds.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {ds.last_synced_at ? `Last sync: ${new Date(ds.last_synced_at).toLocaleString()}` : 'Never synced'}
                  </div>
                  <AnchorLine ds={ds} nodesById={nodesById} />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {ds.target_model ? (
                    <Link href={data_admin_import_path(ds.id)} className="flex flex-col underline decoration-link/30 hover:decoration-link">
                      <span className="font-mono text-xs text-link">{ds.target_model}</span>
                      {ds.target_count != null && (
                        <span className="text-xs text-link/60">{ds.target_count.toLocaleString()} rows</span>
                      )}
                    </Link>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground font-mono text-xs">
                  <SourceLink ds={ds} />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {ds.sync_interval || 'Manual'}
                </TableCell>
                <TableCell className="text-right">
                  <ActionsDropdown

                    onSync={() => onSync(ds)}
                    onEdit={() => onEdit(ds)}
                    onDelete={() => onDelete(ds)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

function IncrementalDataSourceTable({
  dataSources,
  nodesById,
  onSync,
  onEdit,
  onDelete,
  onReset,
}: {
  dataSources: DataSource[]
  nodesById: Record<string, OrgNode>
  onSync: (ds: DataSource) => void
  onEdit: (ds: DataSource) => void
  onDelete: (ds: DataSource) => void
  onReset: (ds: DataSource) => void
}) {
  if (dataSources.length === 0) return null

  return (
    <Card>
      <CardContent className="p-0">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-48">Target</TableHead>
              <TableHead className="w-24">Source</TableHead>
              <TableHead className="w-24">Interval</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dataSources.map((ds) => (
              <TableRow key={ds.id}>
                <TableCell>
                  <div className="font-medium">{ds.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {ds.last_synced_at ? `Last sync: ${new Date(ds.last_synced_at).toLocaleString()}` : 'Never synced'}
                    {ds.incremental_sync_date && ` · Synced up to: ${ds.incremental_sync_date}`}
                  </div>
                  <AnchorLine ds={ds} nodesById={nodesById} />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {ds.target_model ? (
                    <Link href={data_admin_import_path(ds.id)} className="flex flex-col underline decoration-link/30 hover:decoration-link">
                      <span className="font-mono text-xs text-link">{ds.target_model}</span>
                      {ds.target_count != null && (
                        <span className="text-xs text-link/60">{ds.target_count.toLocaleString()} rows</span>
                      )}
                    </Link>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground font-mono text-xs">
                  <SourceLink ds={ds} />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {ds.sync_interval || 'Manual'}
                </TableCell>
                <TableCell className="text-right">
                  <ActionsDropdown
                    onSync={() => onSync(ds)}
                    onEdit={() => onEdit(ds)}
                    onDelete={() => onDelete(ds)}
                    onReset={() => onReset(ds)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

export default function ImportsIndex({ dataSources, processorClasses, sheetProcessorClasses, targetModels, maxResyncLookbackDays, orgNodes }: Props) {
  const [modal, setModal] = useState<{ open: boolean; dataSource?: DataSource | null }>({
    open: false,
  })
  const [resetTarget, setResetTarget] = useState<DataSource | null>(null)
  const nodesById = useMemo(
    () => Object.fromEntries(orgNodes.map((n) => [n.id, n])),
    [orgNodes]
  )

  const handleDelete = (ds: DataSource) => {
    if (confirm(`Delete "${ds.name}" and all its import history?`)) {
      router.delete(admin_import_path(ds.id))
    }
  }

  const handleSync = (ds: DataSource) => {
    router.post(sync_admin_import_path(ds.id))
  }

  // Snapshot and incremental are explicit; full is the catch-all so a new/unknown
  // strategy never silently vanishes from the page.
  const snapshotSources = dataSources.filter((ds) => ds.sync_strategy === 'snapshot')
  const incrementalSources = dataSources.filter((ds) => ds.sync_strategy === 'incremental')
  const updateOnlySources = dataSources.filter((ds) => ds.sync_strategy === 'update_only')
  const fullSyncSources = dataSources.filter(
    (ds) =>
      ds.sync_strategy !== 'snapshot' &&
      ds.sync_strategy !== 'incremental' &&
      ds.sync_strategy !== 'update_only'
  )

  return (
    <AppLayout
      header={<PageTitle title="Data Import" subtitle="Manage data sources and sync history" />}
      headerActions={
        <Button onClick={() => setModal({ open: true, dataSource: null })}>
          + New Data Source
        </Button>
      }
    >
      <div className="flex flex-col gap-6">
        {dataSources.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            No data sources configured. Create one to start importing data from Metabase or Google Sheets.
          </Card>
        ) : (
          <>
            {fullSyncSources.length > 0 && (
              <div className="flex flex-col gap-2">
                <SyncSectionHeader strategy="full" />
                <DataSourceTable
                  dataSources={fullSyncSources}
                  nodesById={nodesById}
                  onSync={handleSync}
                  onEdit={(ds) => setModal({ open: true, dataSource: ds })}
                  onDelete={handleDelete}
                />
              </div>
            )}

            {snapshotSources.length > 0 && (
              <div className="flex flex-col gap-2">
                <SyncSectionHeader strategy="snapshot" />
                <DataSourceTable
                  dataSources={snapshotSources}
                  nodesById={nodesById}
                  onSync={handleSync}
                  onEdit={(ds) => setModal({ open: true, dataSource: ds })}
                  onDelete={handleDelete}
                />
              </div>
            )}

            {updateOnlySources.length > 0 && (
              <div className="flex flex-col gap-2">
                <SyncSectionHeader strategy="update_only" />
                <DataSourceTable
                  dataSources={updateOnlySources}
                  nodesById={nodesById}
                  onSync={handleSync}
                  onEdit={(ds) => setModal({ open: true, dataSource: ds })}
                  onDelete={handleDelete}
                />
              </div>
            )}

            {incrementalSources.length > 0 && (
              <div className="flex flex-col gap-2">
                <SyncSectionHeader strategy="incremental" />
                <IncrementalDataSourceTable
                  dataSources={incrementalSources}
                  nodesById={nodesById}
                  onSync={handleSync}
                  onEdit={(ds) => setModal({ open: true, dataSource: ds })}
                  onDelete={handleDelete}
                  onReset={(ds) => setResetTarget(ds)}
                />
              </div>
            )}
          </>
        )}
      </div>

      {modal.open && (
        <DataSourceModal
          dataSource={modal.dataSource}
          onClose={() => setModal({ open: false })}
          processorClasses={processorClasses}
          sheetProcessorClasses={sheetProcessorClasses}
          targetModels={targetModels}
          maxResyncLookbackDays={maxResyncLookbackDays}
          orgNodes={orgNodes}
        />
      )}

      {resetTarget && (
        <ResetIncrementalModal
          id={resetTarget.id}
          name={resetTarget.name}
          targetCount={resetTarget.target_count}
          onClose={() => setResetTarget(null)}
        />
      )}
    </AppLayout>
  )
}
